import { isBefore, isSameDay } from "date-fns";

export const getDateButtonClasses = (
  date: Date,
  state: {
    isPastDate: boolean;
    isUnavailable: boolean;
    stopTimeExpiredOnDate: boolean;
    stopTimeBlocked: boolean;
    stayRangeBlockedByStopTime: boolean;
    disabledForDeparture: boolean;
    isInDepartureMode: boolean;
    isStartOrEnd: boolean;
    isInRange: boolean;
    isSameMonth: boolean;
    isToday: boolean;
    selectedStartDate: Date | null;
    selectedEndDate: Date | null;
    hoveredShape?: "left" | "right" | "neutral" | null;
  },
) => {
  const {
    isPastDate,
    isUnavailable,
    stopTimeExpiredOnDate,
    stopTimeBlocked,
    stayRangeBlockedByStopTime,
    disabledForDeparture,
    isInDepartureMode,
    isStartOrEnd,
    isInRange,
    isSameMonth,
    isToday,
    selectedStartDate,
    selectedEndDate,
    hoveredShape,
  } = state;

  // In departure-mode the unavailability of `date` itself is not a reason to
  // render it as blocked — the range check (disabledForDeparture) decides.
  // Exception: dates before the selected arrival that are unavailable/restricted
  // should still show as blocked — clicking them would reset to a restricted arrival.
  const unavailableBlocksRender =
    isUnavailable &&
    (!isInDepartureMode ||
      (!!selectedStartDate && isBefore(date, selectedStartDate)));
  const isActuallyDisabled = isPastDate || unavailableBlocksRender;
  // In departure mode a stop-time on the departure day itself should not look
  // blocked if the stayed range [arrival, departure) is still valid.
  const stopTimeBlocksRender =
    stopTimeBlocked &&
    !(
      isInDepartureMode &&
      stopTimeExpiredOnDate &&
      !stayRangeBlockedByStopTime &&
      !disabledForDeparture
    );
  const isSelectable = !isActuallyDisabled && !stopTimeBlocksRender;
  const shouldShowStopTimeDot = stopTimeExpiredOnDate &&
    !(isInDepartureMode && !stayRangeBlockedByStopTime && !disabledForDeparture);
  const isNotSelected = !isStartOrEnd && !isInRange;
  // Keep the same red strikethrough visual for dates the user can't pick as
  // departure either — testers asked for the unavailability markers to stay
  // visible after picking arrival (just conceptually shifted: arrival mode
  // strikes through "can't arrive here", departure mode strikes through
  // "can't depart here"). Selected/in-range cells override below.
  const showBlockedRed =
    (unavailableBlocksRender ||
      (isInDepartureMode &&
        disabledForDeparture &&
        !stopTimeExpiredOnDate &&
        !!selectedStartDate &&
        !isBefore(date, selectedStartDate) &&
        !isSameDay(date, selectedStartDate))) &&
    !isPastDate &&
    isNotSelected;

  return {
    // A selected start/end date must always look selected, even if it landed
    // on an unavailable date (legitimate when departure = arrival + 1 and that
    // day has no arrival availability — the night before is what was booked).
    "!bg-primary !text-on-primary": (isStartOrEnd || isInRange) && !isPastDate,
    "cursor-not-allowed text-ink-secondary opacity-50":
      isPastDate && isNotSelected,
    "cursor-not-allowed text-status-error line-through": showBlockedRed,
    "cursor-not-allowed text-ink-secondary opacity-60":
      stopTimeBlocksRender && isNotSelected && isSameMonth,
    "cursor-not-allowed text-ink-secondary":
      disabledForDeparture &&
      isSelectable &&
      !stopTimeExpiredOnDate &&
      isNotSelected,
    "text-ink-secondary":
      !isSameMonth && isSelectable && !disabledForDeparture,
    "text-ink": isSameMonth && isSelectable && isNotSelected && !isToday,
    "rounded-l-full":
      isStartOrEnd &&
      selectedStartDate &&
      isSameDay(date, selectedStartDate) &&
      !isPastDate,
    "rounded-r-full":
      isStartOrEnd &&
      selectedEndDate &&
      isSameDay(date, selectedEndDate) &&
      !isPastDate,
    "rounded-full":
      isNotSelected && isSelectable && !isToday && hoveredShape !== "left" && hoveredShape !== "right",
    "rounded-full !bg-border !text-ink":
      isToday && isNotSelected && isSelectable && hoveredShape !== "left" && hoveredShape !== "right",
    "bg-surface-hover": hoveredShape === "neutral" && isNotSelected && isSelectable,
    "bg-surface-hover rounded-l-full rounded-r-none":
      hoveredShape === "left" && isNotSelected && isSelectable,
    "bg-surface-hover rounded-r-full rounded-l-none":
      hoveredShape === "right" && isNotSelected && isSelectable,
    "after:absolute after:right-[15%] after:top-2 after:h-1 after:w-1 after:rounded-circle after:bg-ink after:content-['']":
      shouldShowStopTimeDot && isNotSelected,
  };
};
